import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { RoleBadge } from "@/components/RoleBadge";
import { AIAssistantPanel } from "@/components/AIAssistantPanel";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Building2, Users, Wallet, FileText, Clock, CalendarDays,
  Sparkles, Moon, Sun, LogOut, Menu, X, FileSignature, UserRound, Briefcase, Landmark, ClipboardList, FileSpreadsheet, KeyRound, Receipt, DollarSign, Printer, BarChart3, Inbox, Settings2, BookOpen, Megaphone, Network,
} from "lucide-react";
import { Code2 } from "lucide-react";
import { ProfileDialog } from "@/components/ProfileDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { NotificationBell } from "@/components/NotificationBell";
import { HelpButton } from "@/components/HelpButton";
import { stopImpersonating } from "@/lib/impersonate";
import { Eye, ChevronDown } from "lucide-react";
import { moduleFor, canView, canEdit, isCustomRole, MODULE_LABEL } from "@/lib/access";
import { ClientSearch } from "@/components/ClientSearch";

const TAB_COLORS = ["bg-blue-600", "bg-emerald-600", "bg-violet-600", "bg-amber-500", "bg-slate-700"];
const G = { home: "", clients: "Clients", payroll: "Payroll", people: "People", tax: "Tax & Compliance", money: "Billing", growth: "Growth", admin: "Admin" };
const GC = {
  home: { text: "text-sky-400", dot: "bg-sky-400", active: "bg-sky-500 text-white", icon: "text-sky-400" },
  clients: { text: "text-blue-400", dot: "bg-blue-400", active: "bg-blue-600 text-white", icon: "text-blue-400" },
  payroll: { text: "text-emerald-400", dot: "bg-emerald-400", active: "bg-emerald-600 text-white", icon: "text-emerald-400" },
  people: { text: "text-violet-400", dot: "bg-violet-400", active: "bg-violet-600 text-white", icon: "text-violet-400" },
  tax: { text: "text-rose-400", dot: "bg-rose-400", active: "bg-rose-600 text-white", icon: "text-rose-400" },
  money: { text: "text-amber-400", dot: "bg-amber-400", active: "bg-amber-600 text-white", icon: "text-amber-400" },
  growth: { text: "text-teal-400", dot: "bg-teal-400", active: "bg-teal-600 text-white", icon: "text-teal-400" },
  admin: { text: "text-slate-400", dot: "bg-slate-400", active: "bg-slate-600 text-white", icon: "text-slate-400" },
};
const gc = (g) => GC[g] || GC.admin;
const NAV = {
  bureau_admin: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, group: "home" },
    { to: "/clients", label: "Clients", icon: Building2, group: "clients" },
    { to: "/client-setup", label: "Client Setup", icon: ClipboardList, group: "clients" },
    { to: "/payroll", label: "Payroll", icon: Wallet, group: "payroll" },
    { to: "/payslips", label: "Paystubs", icon: FileText, group: "payroll" },
    { to: "/change-requests", label: "Change Requests", icon: FileSignature, group: "payroll" },
    { to: "/direct-deposit", label: "Direct Deposit", icon: Landmark, group: "payroll" },
    { to: "/check-printing", label: "Check Printing", icon: Printer, group: "payroll" },
    { to: "/employees", label: "Employees", icon: Users, group: "people" },
    { to: "/hr", label: "HR Center", icon: Network, group: "people" },
    { to: "/leave", label: "Leave", icon: CalendarDays, group: "people" },
    { to: "/scheduling", label: "Scheduling", icon: CalendarDays, group: "people" },
    { to: "/announcements", label: "Messages", icon: Megaphone, group: "people" },
    { to: "/hiring", label: "Hiring", icon: Briefcase, group: "people" },
    { to: "/tax-impound", label: "Tax Impound", icon: Landmark, group: "tax" },
    { to: "/year-end", label: "Year End", icon: FileSpreadsheet, group: "tax" },
    { to: "/reports", label: "Reports", icon: BarChart3, group: "tax" },
    { to: "/invoices", label: "Invoices", icon: Receipt, group: "money" },
    { to: "/billing", label: "Client Billing", icon: DollarSign, group: "money" },
    { to: "/leads", label: "Leads", icon: Inbox, group: "growth" },
    { to: "/marketing", label: "Marketing", icon: Megaphone, group: "growth" },
    { to: "/settings", label: "Admin Settings", icon: Settings2, group: "admin" },
  ],
  hr_manager: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, group: "home" },
    { to: "/payroll", label: "Payroll", icon: Wallet, group: "payroll" },
    { to: "/payslips", label: "Paystubs", icon: FileText, group: "payroll" },
    { to: "/change-requests", label: "Payroll Changes", icon: FileSignature, group: "payroll" },
    { to: "/direct-deposit", label: "Direct Deposit", icon: Landmark, group: "payroll" },
    { to: "/employees", label: "Employees", icon: Users, group: "people" },
    { to: "/hr", label: "HR Center", icon: Network, group: "people" },
    { to: "/leave", label: "Leave Approvals", icon: CalendarDays, group: "people" },
    { to: "/scheduling", label: "Scheduling", icon: CalendarDays, group: "people" },
    { to: "/announcements", label: "Messages", icon: Megaphone, group: "people" },
    { to: "/hiring", label: "Hiring", icon: Briefcase, group: "people" },
    { to: "/tax-impound", label: "Tax Deposits", icon: Landmark, group: "tax" },
    { to: "/year-end", label: "Year End", icon: FileSpreadsheet, group: "tax" },
    { to: "/reports", label: "Reports", icon: BarChart3, group: "tax" },
    { to: "/invoices", label: "Invoices", icon: Receipt, group: "money" },
    { to: "/access", label: "Roles & Access", icon: KeyRound, group: "admin" },
    { to: "/api-access", label: "API Access", icon: Code2, group: "admin" },
  ],
  processor: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, group: "home" },
    { to: "/payroll", label: "Payroll", icon: Wallet, group: "payroll" },
    { to: "/payslips", label: "Paystubs", icon: FileText, group: "payroll" },
    { to: "/change-requests", label: "Payroll Changes", icon: FileSignature, group: "payroll" },
    { to: "/direct-deposit", label: "Direct Deposit", icon: Landmark, group: "payroll" },
    { to: "/employees", label: "Employees", icon: Users, group: "people" },
    { to: "/hr", label: "HR Center", icon: Network, group: "people" },
    { to: "/tax-impound", label: "Tax Deposits", icon: Landmark, group: "tax" },
    { to: "/year-end", label: "Year End", icon: FileSpreadsheet, group: "tax" },
    { to: "/reports", label: "Reports", icon: BarChart3, group: "tax" },
  ],
  employee: [
    { to: "/dashboard", label: "My Dashboard", icon: LayoutDashboard },
    { to: "/punch", label: "Clock In / Out", icon: Clock },
    { to: "/me", label: "My Information", icon: UserRound },
    { to: "/my-invoices", label: "Contractor Portal", icon: Receipt, contractorOnly: true },
    { to: "/timesheet", label: "Timesheet", icon: Clock },
    { to: "/my-schedule", label: "My Schedule", icon: CalendarDays },
    { to: "/messages", label: "Messages", icon: Megaphone },
    { to: "/payslips", label: "My Paystubs", icon: FileText },
    { to: "/leave", label: "Time Off", icon: CalendarDays },
    { to: "/my-report", label: "My Report", icon: BarChart3 },
  ],
};

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { companies, activeCompanyId, selectCompany, activeCompany } = useCompany();
  const navigate = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!["bureau_admin", "hr_manager", "processor"].includes(user?.role)) return;
    const params = user.role === "bureau_admin" && activeCompanyId ? { company_id: activeCompanyId } : {};
    api.get("/change-requests/unread", { params }).then((r) => setUnread(r.data.requests)).catch(() => {});
  }, [user?.role, activeCompanyId, location.pathname]);
  const [msgUnread, setMsgUnread] = useState(0);
  useEffect(() => {
    if (user?.role !== "employee") return;
    const f = () => api.get("/announcements/inbox/unread-count").then((r) => setMsgUnread(r.data.unread)).catch(() => {});
    f(); window.addEventListener("rp-messages-read", f); return () => window.removeEventListener("rp-messages-read", f);
  }, [user?.role, location.pathname]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  useEffect(() => { if (user?.must_change_password) setPwOpen(true); }, [user?.must_change_password]);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const MODULE_ROUTES = { tax_impound: ["/tax-impound"], hiring: ["/hiring"], time_attendance: ["/timesheets", "/punch", "/timesheet"], hr: ["/hr"] };
  const mods = activeCompany?.modules;
  const nav = (NAV[user.role] || []).filter((n) => !n.contractorOnly || user.worker_type === "contractor").filter((n) => !mods || !Object.entries(MODULE_ROUTES).some(([k, routes]) => !mods[k] && routes.includes(n.to))).filter((n) => canView(user, moduleFor(n.to)));
  const curMod = moduleFor(location.pathname);
  const viewOnly = isCustomRole(user) && curMod && !canEdit(user, curMod);
  const groups = Object.entries(nav.reduce((acc, n) => { const g = n.group || "home"; (acc[g] = acc[g] || []).push(n); return acc; }, {}));
  const [collapsed, setCollapsed] = useState(() => { try { return JSON.parse(localStorage.getItem("navCollapsed") || "{}"); } catch { return {}; } });
  const toggleGroup = (g) => setCollapsed((c) => { const n = { ...c, [g]: !c[g] }; localStorage.setItem("navCollapsed", JSON.stringify(n)); return n; });

  const SidebarInner = () => (
    <>
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
        <div className="bg-white rounded-lg px-2 py-1.5 flex items-center justify-center w-full max-w-[180px]">
          <img src="/rightpay-logo.png" alt="RightPay" className="h-10 w-auto object-contain" />
        </div>
      </div>
      <nav className="flex-1 p-3 pt-1 overflow-y-auto" data-testid="sidebar-nav">
        {groups.map(([g, items]) => (
          <div key={g} data-testid={`nav-group-${g}`} className="space-y-0.5">
            {G[g] && (
              <button type="button" data-testid={`nav-group-toggle-${g}`} aria-expanded={!collapsed[g]} onClick={() => toggleGroup(g)} className={`w-full flex items-center justify-between px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${gc(g).text} hover:brightness-125 transition-[filter]`}>
                <span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${gc(g).dot}`} />{G[g]}</span>
                <span className="flex items-center gap-1.5">{collapsed[g] && items.some((n) => location.pathname.startsWith(n.to)) && <span className="w-1.5 h-1.5 rounded-full bg-white" />}{collapsed[g] && unread > 0 && items.some((n) => n.to === "/change-requests") && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${collapsed[g] ? "-rotate-90" : ""}`} /></span>
              </button>
            )}
            {!(G[g] && collapsed[g]) && items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setMobileNav(false)}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? `${gc(g).active} shadow-sm`
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (<>
                <n.icon className={`w-[18px] h-[18px] ${isActive ? "" : gc(g).icon}`} />
                {n.label}
                {n.to === "/change-requests" && unread > 0 && <span data-testid="nav-unread-badge" className="ml-auto text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white grid place-items-center">{unread}</span>}
                {n.to === "/messages" && msgUnread > 0 && <span data-testid="nav-messages-badge" className="ml-auto text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white grid place-items-center">{msgUnread}</span>}
                </>)}
              </NavLink>
            ))}
          </div>
        ))}
        <NavLink to="/help" data-testid="nav-help" onClick={() => setMobileNav(false)} className={({ isActive }) => `mt-4 flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white text-[#0b1a3a] shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
          <BookOpen className="w-[18px] h-[18px]" />
          Help & Manuals
        </NavLink>
      </nav>
      <div className="p-3 space-y-2">
        <button
          data-testid="ai-assistant-trigger-btn"
          onClick={() => setAiOpen(true)}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-blue-600 to-sky-400 text-white hover:opacity-90 transition-opacity"
        >
          <Sparkles className="w-[18px] h-[18px]" />
          AI Assistant
        </button>
        {(user.role === "bureau_admin" || user.role === "hr_manager" || user.role === "processor") && (
          <a
            data-testid="open-timeclock-link"
            href="/timeclock"
            target="_blank"
            rel="noreferrer"
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium border border-white/15 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Clock className="w-[18px] h-[18px]" />
            Open Time Clock
          </a>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside data-testid="sidebar" className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0b1a3a] border-r border-[#0b1a3a] z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileNav(false)} />
          <aside className="absolute inset-y-0 left-0 w-[85vw] max-w-72 flex flex-col bg-[#0b1a3a] border-r border-[#0b1a3a] overflow-y-auto animate-in slide-in-from-left duration-200">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-20 h-14 sm:h-16 flex items-center gap-2 sm:gap-3 px-3 sm:px-6 border-b border-border bg-card/80 backdrop-blur-md">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileNav(true)}>
            <Menu className="w-5 h-5" />
          </Button>

          {user.role === "bureau_admin" || user.role === "processor" ? (<>
            <ClientSearch />
            <Select value={activeCompanyId || ""} onValueChange={selectCompany}>
              <SelectTrigger data-testid="client-company-selector" className="w-[150px] sm:w-[240px] font-medium truncate">
                <SelectValue placeholder="Select client…" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>
                    {c.logo_url ? <img src={c.logo_url} alt="" className="inline w-4 h-4 object-contain mr-1.5 align-middle" /> : <span className="mr-1.5">{c.logo}</span>} {c.name}{c.client_id && <span className="ml-1.5 text-[10px] font-mono-num text-muted-foreground">#{c.client_id}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>) : (
            <div className="flex items-center gap-2 font-display font-semibold" data-testid="header-company-brand">
              <CompanyLogo company={activeCompany} size="sm" />
              {activeCompany?.name || "Workspace"}{activeCompany?.client_id && <span className="ml-1.5 text-[10px] font-mono-num text-muted-foreground" data-testid="active-client-id">#{activeCompany.client_id}</span>}
            </div>
          )}

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            {viewOnly && <span data-testid="view-only-badge" className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300"><Eye className="w-3 h-3" /> View only · {MODULE_LABEL[curMod]}</span>}
            <HelpButton />
            <NotificationBell />
            <Button data-testid="theme-toggle" variant="ghost" size="icon" onClick={() => setDark((d) => !d)}>
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-accent transition-colors">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user.email}</div>
                  <div className="mt-2"><RoleBadge role={user.role} /></div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="profile-menu-btn" onClick={() => setProfileOpen(true)}>
                  <UserRound className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="change-password-btn" onClick={() => setPwOpen(true)}>
                  <KeyRound className="w-4 h-4 mr-2" /> Change Password
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {user.impersonating && (
          <div data-testid="impersonation-banner" className="sticky top-14 sm:top-16 z-20 flex items-center justify-between gap-3 px-3 sm:px-6 py-2 bg-amber-500 text-amber-950 text-sm font-medium">
            <span className="flex items-center gap-2 min-w-0"><Eye className="w-4 h-4 shrink-0" /> <span className="truncate">Viewing as <b>{user.name || user.email}</b><span className="hidden sm:inline"> ({user.role.replace("_", " ")}) — actions here are recorded as impersonation by {user.impersonated_by}.</span></span></span>
            <Button size="sm" variant="secondary" className="shrink-0" data-testid="stop-impersonation-btn" onClick={stopImpersonating}>Stop viewing</Button>
          </div>
        )}

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto min-w-0 overflow-x-hidden pb-24 lg:pb-6">{children}</main>

        {/* Mobile bottom tab bar */}
        <nav data-testid="mobile-tabbar" className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border flex items-stretch gap-1 px-1.5 pt-1.5 pb-[calc(env(safe-area-inset-bottom)+0.375rem)]">
          {nav.slice(0, 4).map((n, i) => (
            <NavLink key={n.to} to={n.to} data-testid={`tab-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl text-[10px] font-semibold min-w-0 text-white transition-transform active:scale-95 ${TAB_COLORS[i % TAB_COLORS.length]} ${isActive ? "ring-2 ring-offset-1 ring-offset-card ring-foreground/40 shadow-md" : "opacity-90"}`}>
              <n.icon className="w-5 h-5" /><span className="truncate max-w-full">{n.label}</span>
            </NavLink>
          ))}
          <button data-testid="tab-more" onClick={() => setMobileNav(true)} className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl text-[10px] font-semibold text-white active:scale-95 transition-transform ${TAB_COLORS[4]}`}><Menu className="w-5 h-5" />More</button>
        </nav>
      </div>

      <AIAssistantPanel open={aiOpen} onOpenChange={setAiOpen} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} forced={!!user?.must_change_password} />
    </div>
  );
}
